/**
 * Onboarding-chain mutation hooks for the three "Configure Your AI" screens
 * (details form, file upload, website URL). Each screen's flow is a
 * sequential dependent chain — POST /users/onboarding must succeed before
 * the account record is updated — so each is modeled as ONE useMutation
 * whose mutationFn awaits both calls internally (mirrors queries/tasks.js's
 * useGenerateTaskPrompt), rather than two mutations the caller would have to
 * sequence itself.
 *
 * Per-screen payload shapes for both calls are built by the caller (they
 * differ enough — including, across ConfigureYourAIUpload's two upload
 * paths, differing key-spread order that changes which value wins on
 * conflict — that reconstructing them here risked altering existing
 * behavior). Only the "POST onboarding, then update the account" chain
 * itself is shared.
 */
import { useMutation } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT } = ReqMethods;

async function submitOnboardingThenUpdateAccount(onboardingPayload, accountsPayload) {
  await request(POST, "/users/onboarding", { payload: onboardingPayload });
  return request(PUT, "/accounts", { payload: accountsPayload });
}

// ConfigureYourAIDetail.jsx — "who/about/achieve" form.
export function useConfigureAIDetail() {
  return useMutation({
    mutationFn: ({ onboardingPayload, accountsPayload }) =>
      submitOnboardingThenUpdateAccount(onboardingPayload, accountsPayload),
  });
}

// ConfigureYourAIUpload.jsx — dropzone upload + Google Drive/Dropbox paths.
// The actual file upload (useUsersContext().contextUploadFileOnboarding) is
// a separate, upstream step the caller awaits before invoking this mutation.
export function useConfigureAIUpload() {
  return useMutation({
    mutationFn: ({ onboardingPayload, accountsPayload }) =>
      submitOnboardingThenUpdateAccount(onboardingPayload, accountsPayload),
  });
}

// ConfigureYourAIWeb.jsx — website-url form. Its granular error-message
// branching (network-error regex, backend error-shape checks) stays in the
// component's catch block around mutateAsync, unchanged.
export function useConfigureAIWeb() {
  return useMutation({
    mutationFn: ({ onboardingPayload, accountsPayload }) =>
      submitOnboardingThenUpdateAccount(onboardingPayload, accountsPayload),
  });
}

// Shared by all three screens' YoureAllSet onComplete — checks whether a
// premium, no-subscription account needs to be routed to /auth/add-payment
// before /app/chat. The old useAppServices() call site passed { plan } to
// CallService, whose options destructuring only recognizes
// {query, payload, token, apiKey} — so `plan` was silently dropped and the
// request was always sent as a bare GET /saas/plan with no id filter. Ported
// exactly as-is (still no query param) rather than "fixing" it by adding one,
// since that's a pre-existing, unrelated bug outside this migration's scope.
export function useCheckSaasPlan() {
  return useMutation({
    mutationFn: () => request(GET, "/saas/plan"),
  });
}
